import React from 'react';
import { AskAiBookingStatus } from '../types';

function money(value?: number | null): string {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return '';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

function minutesLabel(minutes?: number): string {
  const n = Number(minutes) || 0;
  if (n < 1) return '';
  if (n < 60) return `${n} min`;
  const hours = Math.floor(n / 60);
  const rest = n % 60;
  return rest ? `${hours} hr ${rest} min` : `${hours} hr`;
}

function dateLabel(ymd?: string): string {
  if (!ymd) return '';
  const parsed = new Date(`${ymd}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return ymd;
  return parsed.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
}

function AskAiBookingReviewCard({
  booking,
  textColor,
  title,
}: {
  booking: AskAiBookingStatus;
  textColor: string;
  title: string;
}) {
  const services = booking.services || [];
  const totalFromServices = services.reduce((sum, service) => sum + (Number(service.price) > 0 ? Number(service.price) : 0), 0);
  const total = booking.totalPrice && booking.totalPrice > 0 ? booking.totalPrice : totalFromServices;
  const totalDuration = services.reduce((sum, service) => sum + (Number(service.durationMinutes) || 0), 0);
  const muted = `${textColor}99`;
  const line = `${textColor}18`;
  const techNames: string[] = [];
  for (const service of services) {
    const name = service.professionalName;
    if (name && !techNames.includes(name)) techNames.push(name);
  }
  const mixedTechs = techNames.length > 1;
  const rows: Array<{ label: string; value: string; stack?: boolean }> = [];
  const outsideHours = Boolean(
    booking.outsideOpenings
    || /outside the usual openings/i.test(booking.summary || '')
  );
  if (booking.quote) {
    rows.push({
      label: 'Details',
      value: booking.quoteDetails || '—',
      stack: true,
    });
    rows.push({
      label: 'Schedule',
      value: booking.skipPreferredSchedule || !booking.preferredSchedule ? 'No preference' : booking.preferredSchedule,
    });
    if ((booking.quoteImageUrls || []).length > 0 || (booking.quoteAttachmentIds || []).length > 0) {
      const count = Math.max(booking.quoteImageUrls?.length || 0, booking.quoteAttachmentIds?.length || 0);
      rows.push({ label: 'Photos', value: `${count} attached` });
    }
  } else {
    if (booking.customerName) {
      const walkIn = Boolean(booking.newCustomer) && !booking.customerId;
      rows.push({
        label: 'Customer',
        value: walkIn ? `${booking.customerName} (new customer)` : booking.customerName,
      });
    }
    if (!mixedTechs && (booking.professionalName || techNames[0])) {
      rows.push({ label: 'Professional', value: booking.professionalName || String(techNames[0]) });
    }
    if (booking.date || dateLabel(booking.date)) rows.push({ label: 'Date', value: dateLabel(booking.date) || booking.date || '' });
    if (booking.timeLabel || booking.time) rows.push({ label: 'Time', value: booking.timeLabel || booking.time || '' });
    if (booking.recurring) {
      const weeks = Number(booking.intervalWeeks);
      const visits = Number(booking.occurrenceCount);
      if (weeks >= 1) rows.push({ label: 'Repeats', value: weeks === 1 ? 'Every week' : `Every ${weeks} weeks` });
      if (visits >= 1) rows.push({ label: 'Visits', value: `${visits}` });
    }
    if (booking.serviceAddress?.address || booking.serviceAddress?.city) {
      const addr = booking.serviceAddress;
      const cityStateZip = [addr.city, [addr.state, addr.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
      const addressLine = [addr.address, cityStateZip].filter(Boolean).join(', ');
      rows.push({ label: 'On-site location', value: addr.locationNotes ? `${addressLine} (${addr.locationNotes})` : addressLine });
    }
    if (totalDuration > 0) rows.push({ label: 'Duration', value: booking.recurring ? `${minutesLabel(totalDuration)} each` : minutesLabel(totalDuration) });
    if (total > 0) rows.push({ label: 'Total', value: money(total) });
    if (booking.privateNotes) rows.push({ label: 'Private notes', value: booking.privateNotes, stack: true });
  }

  const showFooter = Boolean(booking.depositRequired && booking.depositAmount && !booking.revising && !booking.quote) || outsideHours;

  return (
    <div className="mt-2 overflow-hidden" style={{ border: `1px solid ${line}`, borderRadius: 12, background: `${textColor}06` }}>
      <div className="px-3 py-2 text-xs font-semibold tracking-wide uppercase" style={{ color: muted, borderBottom: `1px solid ${line}` }}>
        {title}
      </div>
      {services.length > 0 && (
        <ul className="m-0 px-3 py-2 list-none" style={{ borderBottom: rows.length || showFooter ? `1px solid ${line}` : undefined }}>
          {services.map((service) => {
            const meta = service.quotable
              ? 'Quote'
              : [minutesLabel(service.durationMinutes), money(service.price)].filter(Boolean).join(' · ');
            return (
              <li key={service.id || service.name} className="flex items-start justify-between gap-3 py-1">
                <span className="leading-snug">
                  <span className="font-medium">{service.name}</span>
                  {mixedTechs && service.professionalName ? (
                    <span className="block text-xs" style={{ color: muted }}>{service.professionalName}</span>
                  ) : null}
                </span>
                {meta ? <span className="text-xs flex-shrink-0 pt-0.5" style={{ color: muted }}>{meta}</span> : null}
              </li>
            );
          })}
        </ul>
      )}
      {rows.length > 0 && (
        <dl className="m-0 px-3 py-2">
          {rows.map((row) => (
            <div key={row.label} className={row.stack ? 'py-0.5' : 'flex items-baseline justify-between gap-3 py-0.5'}>
              <dt className="text-xs m-0" style={{ color: muted }}>{row.label}</dt>
              <dd className={`m-0 text-sm font-medium ${row.stack ? 'whitespace-pre-wrap mt-0.5' : 'text-right'}`}>{row.value}</dd>
            </div>
          ))}
        </dl>
      )}
      {booking.recurring && (booking.recurringOccurrences || []).length > 0 ? (
        <ul className="m-0 px-3 py-2 list-none" style={{ borderTop: `1px solid ${line}` }}>
          {(booking.recurringOccurrences || []).map((item) => (
            <li key={`${item.occurrenceIndex}-${item.scheduledAt}`} className="flex items-start justify-between gap-3 py-0.5">
              <span className="text-sm leading-snug">{item.label}</span>
              <span className="text-xs flex-shrink-0 pt-0.5" style={{ color: item.status === 'CONFLICT' ? '#b91c1c' : muted }}>
                {item.status === 'CONFLICT' ? 'Conflict' : 'Open'}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
      {booking.depositRequired && booking.depositAmount && !booking.revising && !booking.quote ? (
        <p className="m-0 px-3 py-2 text-xs leading-relaxed" style={{ color: muted, borderTop: `1px solid ${line}` }}>
          Deposit {money(booking.depositAmount)} due
          {booking.depositDueLabel ? ` within ${booking.depositDueLabel}` : ''} after booking
          {booking.depositNonRefundable ? '. Non-refundable if cancelled.' : '.'}
        </p>
      ) : null}
      {outsideHours ? (
        <p className="m-0 px-3 py-2 text-xs leading-relaxed" style={{ color: muted, borderTop: `1px solid ${line}` }}>
          This time is outside the usual openings, and it will still be booked.
        </p>
      ) : null}
    </div>
  );
}

export default AskAiBookingReviewCard;
